<template>
  <div>
    <app-spinner v-if="!is_loaded"/>
    <template v-else>
      <app-cells position="between">
        <h1 class="title">Payment request ({{ $route.params.id }})</h1>
      </app-cells>
      <div class="info-cards">
        <div class="info-cards__card">
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Supplier:</span>
              <span  @click="onCopyToClipboard(payment_data.supplier.legal_name)">
                {{ payment_data.supplier.legal_name }}
              </span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Account:</span>
              <span @click="onCopyToClipboard(payment_data.supplier_payment_method.public_name)">
                {{ payment_data?.supplier_payment_method?.public_name || '-' }}
              </span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Amount:</span>
              <span>{{ amountFormat(payment_data.amount) }}$</span>
              <a v-if="payment_data.attachment" class="info-cards__file-download" :href="payment_data.attachment.file" target="_blank">
                (POP)
              </a>
            </div>
          </div>
        </div>
      </div>
      <app-cells>
        <app-input
          v-model="search"
          class="input--300"
          name="search"
          placeholder="Search by lead name"
          type="search"
          @input.native="onLeadSearch"
        />
        <app-select
          @input="fetchPaymentPlanItems"
          v-model="filter_hotel"
          :options="hotels_options"
          :reduce="(item) => item.id"
          class="select--300"
          placeholder="Choose hotel"
        />
      </app-cells>
      <v-client-table
        :class="{ 'table-default--loading': table_preloader }"
        :columns="table_payment_plan.columns"
        :data="table_payment_plan.items"
        :options="table_payment_plan.options"
        class="table-default"
      >
        <template #h__payment>
          <div class="table-default__left">Payment</div>
        </template>
        <template #h__hotel>
          <div class="table-default__left">Hotel</div>
        </template>
        <template #h__client>
          <div class="table-default__left">Client</div>
        </template>
        <template #type="{ row }">
          <span
            v-if="row.type === 'deposit'"
            :style="{ backgroundColor: '#439ad1' }"
            class="status-tag"
          >
            {{ row.type[0].toUpperCase() + row.type.slice(1) }}
          </span>
          <span
            v-if="row.type === 'final'"
            :style="{ backgroundColor: '#77b017' }"
            class="status-tag"
          >
            {{ row.type[0].toUpperCase() + row.type.slice(1) }}
          </span>
        </template>
        <template #date="{ row }">
          <span
            v-if="row.category === 'overdue'"
            :style="{ backgroundColor: '#e85c20' }"
            class="status-tag"
          >
            {{ dateFormat(row.date) }}
          </span>
          <span
            v-if="row.category === 'upcoming'"
            :style="{ backgroundColor: '#439ad1' }"
            class="status-tag"
          >
            {{ dateFormat(row.date) }}
          </span>
          <template v-if="!row.category">
            {{ dateFormat(row.date) }}
          </template>
        </template>
        <template #payment="{ row }">
          <div class="table-default__left">
            {{ Number(row.amount).toLocaleString() }}$
            <span
              v-if="row.reservation_hotels.abnormal_price.abnormal_price && !row.reservation_hotels.abnormal_price.verified"
              class="status-tag status-tag--small"
              style="background-color: #e85c20"
            >
              Abnormal
            </span>
            <span
              v-if="row.reservation_hotels.abnormal_price.abnormal_price && row.reservation_hotels.abnormal_price.verified"
              class="status-tag status-tag--small"
              style="background-color: #f0c427"
              v-tooltip="{ content: row.reservation_hotels.abnormal_price.notes || 'N/A', placement: 'top-start', offset: 10 }"
            >
              Abnormal
            </span>
          </div>
        </template>
        <template #hotel="{ row }">
          <div class="table-default__left">
            <div class="table-default__controls">
              <router-link
                :to="{ name: 'reservation-hotels-finance', params: { id: row.reservation_hotels.id }, }"
                class="table-default__link-border"
              >
                {{ row.reservation_hotels.hotel.name }} (#{{ row.reservation_hotels.id }})
              </router-link>
            </div>
          </div>
        </template>
        <template #client="{ row }">
          <div class="table-default__left">
            <div v-if="is_general_manager" class="table-default__controls">
              <a
                :href="`https://story.altezza.travel/clients/${row.reservation_hotels.lead?.final_itinerary?.itinerary_id}`"
                target="_blank"
                class="table-default__activity table-default__activity--itinerary"
                :class="{ 'table-default__activity--active': row.reservation_hotels.lead?.final_itinerary?.itinerary_id }"
              />
              <router-link
                :to="{ name: 'leads-detail-reservations', params: { id: row.reservation_hotels.lead.id }, }"
                class="table-default__link-border"
              >
                {{ row.reservation_hotels.lead.name }}
              </router-link>
            </div>
            <template v-else>{{ row.reservation_hotels.lead.name }}</template>
          </div>
        </template>
        <template #deposit="{ row }">
          <div class="table-default__left">
            {{ row.reservation_hotels.lead.final_itinerary.invoices_amount_paid_usd.toLocaleString() }}$
          </div>
        </template>
        <template #check_in="{ row }">
          {{ dateFormat(row.reservation_hotels.check_in) }}
        </template>
        <template #invoice="{ row }">
          <template v-if="!row.invoice.attachment">
            {{ Number(row.invoice.amount_total).toLocaleString() }}$
          </template>
          <template v-else>
            <a
              :href="row.invoice.attachment.file"
              class="table-default__link-border table-default__link-border--center"
              target="_blank"
            >
              {{ Number(row.invoice.amount_total).toLocaleString() }}$
            </a>
          </template>
        </template>
        <template #controls="{ row }">
          <div v-if="!payment_data.completed" class="table-default__controls">
            <button @click="onShowDeleteModal(row)" type="button">
              <img src="@/assets/img/delete-table-icon.svg" alt="Delete">
            </button>
          </div>
        </template>
      </v-client-table>
      <app-cells
        :position="pagination_payment_plan.pages > 1 ? 'between' : 'end'"
      >
        <paginate
          v-if="pagination_payment_plan.pages > 1"
          v-model="pagination_payment_plan.page"
          :clickHandler="onClickPaymentPlanPagination"
          :container-class="'pagination'"
          :next-class="'pagination__item pagination__item--next'"
          :next-link-class="'pagination__link pagination__link--next'"
          :next-text="'>'"
          :page-class="'pagination__item'"
          :page-count="pagination_payment_plan.pages"
          :page-link-class="'pagination__link'"
          :prev-class="'pagination__item pagination__item--prev'"
          :prev-link-class="'pagination__link pagination__link--prev'"
          :prev-text="'<'"
        />
        <app-table-records
          :all="pagination_payment_plan.count"
          :count="table_payment_plan.items.length"
          :limit="40"
          :page="pagination_payment_plan.page"
        />
      </app-cells>
      <app-cells v-if="!payment_data.completed" position="end">
        <app-button @click.native="$modal.show('paid-modal')">Paid</app-button>
      </app-cells>
      <app-email-list-multiple-recipient
        v-if="payment_data.completed && payment_data.email"
        :data="payment_data.email"
        :count="0"
      />
    </template>
    <modal
      name="accept-delete-modal"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button
          @click="$modal.hide('accept-delete-modal')"
          class="popup__close"
          type="button"
        ></button>
        <div class="popup__title popup__title--flex">
          <img src="@/assets/img/warning-round-icon.svg" alt=""/>
          Delete payment
        </div>
        <div class="popup__body">
          <div class="popup__subt">
            Are you sure you want to deleted this payment?
          </div>
          <app-cells position="center" :indent="false">
            <app-button @click.native="$modal.hide('accept-delete-modal')" theme="dark">Cancel</app-button>
            <app-button @click.native="onClickPaymentDelete" ref="delete">Yes</app-button>
          </app-cells>
        </div>
      </div>
    </modal>
    <modal
      name="paid-modal"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button
          @click="$modal.hide('paid-modal')"
          class="popup__close"
          type="button"
        ></button>
        <div class="popup__title">Sending payment</div>
        <div class="popup__body">
          <form @submit.prevent="onSendPaidForm">
            <app-form-group label="Proof of payment" required>
              <app-uploader
                v-model="paid_form.proof_of_payment"
                class="custom-uploader--min-150"
                :extensions="[
                  'application/pdf',
                  'image/jpeg',
                  'image/png',
                  'image/tiff',
                  'image/bmp',
                  'pdf',
                ]"
              />
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button type="submit" ref="paid_form">Send</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import {
  getHotelsListSearch,
  getReservationsPaymentRequest,
  getReservationsPaymentPlanHotels,
  patchReservationsPaymentRequest,
  deleteReservationsPaymentRequestPaymentPlans,
} from '@/http'
import {format, parse, parseISO} from 'date-fns'
import debounce from 'lodash/debounce'
import AppEmailListMultipleRecipient from '@/components/AppEmailListMultipleRecipient.vue'

export default {
  name: 'ReservationPaymentRequestsDetail',
  components: {AppEmailListMultipleRecipient},
  data() {
    return {
      is_loaded: false,
      payment_data: {},
      table_preloader: false,
      payment_completed_loader: false,
      table_payment_plan: {
        items: [],
        columns: ['type', 'date', 'payment', 'hotel', 'client', 'check_in', 'invoice', 'controls'],
        options: {
          headings: {
            type: 'Type',
            date: 'Date',
            payment: 'Payment',
            hotel: 'Hotel',
            client: 'Client',
            check_in: 'Check in',
            invoice: 'Invoice',
            controls: '',
          },
          pagination: {show: false},
          sortable: [],
          perPage: 40,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false,
        },
      },
      pagination_payment_plan: {
        pages: 1,
        page: 1,
        count: 1,
      },
      search: '',
      hotels_options: [],
      filter_hotel: null,
      paid_form: {},
      item_to_delete: null,
    }
  },
  computed: {
    is_general_manager() {
      return this.$store.state.userGroups.map((item) => item.code).includes('general_manager')
    },
  },
  created() {
    this.fetchPaymentPlanItems()
    this.getPaymentData()
    getHotelsListSearch({active: 'true'}).then((res) => {
      this.hotels_options = res.data
    })
  },
  methods: {
    onShowDeleteModal(item) {
      this.item_to_delete = item
      this.$modal.show('accept-delete-modal')
    },
    onSendPaidForm() {
      this.$refs.paid_form.preload = true
      patchReservationsPaymentRequest(this.$route.params.id, {
        completed: true,
        attachment: this.paid_form.proof_of_payment && this.paid_form.proof_of_payment.id ? this.paid_form.proof_of_payment.id : null,
      })
        .finally(() => {
          this.$refs.paid_form.preload = false
        })
        .then(() => {
          this.getPaymentData()
          this.$modal.hide('paid-modal')
          this.$notify({
            type: 'success',
            title: 'Payment paid',
          })
        })
    },
    onCopyToClipboard(string) {
      navigator.clipboard
        .writeText(string)
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Copied to clipboard',
          })
        })
        .catch(() => {
          console.error('Something went wrong')
        })
    },
    onClickPaymentDelete() {
      this.$refs.delete.preload = true
      deleteReservationsPaymentRequestPaymentPlans(this.$route.params.id, this.item_to_delete.id)
        .finally(() => { this.$refs.delete.preload = false })
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Payment deleted',
          })
          this.fetchPaymentPlanItems()
          this.getPaymentData()
          this.$modal.hide('accept-delete-modal')
        })
    },
    getPaymentData() {
      getReservationsPaymentRequest(this.$route.params.id).then((res) => {
        this.payment_data = res.data
        this.is_loaded = true
        this.payment_completed_loader = false
      })
    },
    fetchPaymentPlanItems() {
      this.table_preloader = true
      getReservationsPaymentPlanHotels({
        page: this.pagination_payment_plan.page,
        page_size: 40,
        search: this.search,
        payment_request: this.$route.params.id,
        hotel: this.filter_hotel,
      }).then((res) => {
        this.table_payment_plan.items = res.data.results
        this.pagination_payment_plan.pages = res.data.pages
        this.pagination_payment_plan.count = res.data.count
        this.table_preloader = false
      })
    },
    onClickPaymentPlanPagination(page) {
      this.pagination_payment_plan.page = page
      this.fetchPaymentPlanItems()
    },
    dateFormat(date) {
      return format(parse(date, 'yyyy-MM-dd', new Date()), 'dd MMM yyyy')
    },
    amountFormat(amount) {
      return amount ? new Intl.NumberFormat('ru-RU').format(amount) : '-'
    },
    onLeadSearch(search) {
      this.searchLeadName(search, this)
    },
    searchLeadName: debounce((vm) => {
      vm.pagination_payment_plan.page = 1
      vm.fetchPaymentPlanItems()
    }, 350),
    getUserAvatar(crop_url) {
      if (crop_url) return crop_url
      return require('@/assets/img/temp/avatar-slug.jpg')
    },
    dateTimeFormat(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy HH:mm') : '-'
    },
  },
}
</script>
